import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS, getImageUrl } from '../config/api';

const Catalog = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [cowsData, setCowsData] = useState([]);
  const [cowsLoading, setCowsLoading] = useState(true);
  const [pagination, setPagination] = useState({ limit: 12, offset: 0, total: 0, has_next: false, has_prev: false });
  const navigate = useNavigate();

  const itemsPerPage = 12;

  // Fetch kategori dari API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await apiClient.get(`${API_ENDPOINTS.KATEGORI_PRODUK.LIST}?limit=10&offset=0`);
        
        // Handle struktur response dari API
        // Response format: { status, message, data: { kategori_produks: [...], pagination: {...} } }
        let categoriesFromApi = [];
        
        if (response.data && response.data.kategori_produks && Array.isArray(response.data.kategori_produks)) {
          // Format sesuai API PakMoo
          categoriesFromApi = response.data.kategori_produks;
        } else if (response.data && Array.isArray(response.data)) {
          // Fallback: jika data langsung array
          categoriesFromApi = response.data;
        } else if (Array.isArray(response)) {
          // Fallback: jika response langsung array
          categoriesFromApi = response;
        } else if (response.kategori_produks && Array.isArray(response.kategori_produks)) {
          // Fallback: jika kategori_produks di root level
          categoriesFromApi = response.kategori_produks;
        } else if (response.items && Array.isArray(response.items)) {
          categoriesFromApi = response.items;
        } else if (response.results && Array.isArray(response.results)) {
          categoriesFromApi = response.results;
        }
        
        // Tambahkan opsi "Semua" di awal
        const formattedCategories = [
          { id: 'all', name: 'Semua', icon: 'fa-list' },
          ...categoriesFromApi
            .filter(cat => cat.deleted_at === null) // Filter out deleted items
            .map((cat) => ({
              id: cat.id || `category-${cat.nama}`,
              name: cat.nama || cat.name || 'Kategori',
              icon: 'fa-tag' // Default icon, bisa diubah jika API menyediakan
            }))
        ];
        
        setCategories(formattedCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback ke kategori default jika API error
        setCategories([
          { id: 'all', name: 'Semua', icon: 'fa-list' },
          { id: 'ekonomis', name: 'Ekonomis', icon: 'fa-piggy-bank' },
          { id: 'medium', name: 'Medium', icon: 'fa-balance-scale' },
          { id: 'premium', name: 'Premium', icon: 'fa-star' },
        ]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch produk (sapi) dari API
  useEffect(() => {
    const fetchProduk = async () => {
      try {
        setCowsLoading(true);
        
        // Build query parameters
        const params = new URLSearchParams({
          limit: itemsPerPage.toString(),
          offset: ((currentPage - 1) * itemsPerPage).toString(),
        });
        
        // Tambahkan filter kategori jika bukan 'all'
        if (selectedCategory !== 'all') {
          params.append('id_kategori_produk', selectedCategory);
        }
        
        const response = await apiClient.get(`${API_ENDPOINTS.PRODUK.PUBLIC_LIST}?${params.toString()}`);
        
        // Handle struktur response dari API
        let produksFromApi = [];
        if (response.data && response.data.produks && Array.isArray(response.data.produks)) {
          produksFromApi = response.data.produks;
          
          // Update pagination
          if (response.data.pagination) {
            setPagination(response.data.pagination);
          }
        }
        
        // Fetch images untuk setiap produk
        const produksWithImages = await Promise.all(
          produksFromApi
            .filter(produk => produk.deleted_at === null && produk.is_available !== false)
            .map(async (produk) => {
              let imageUrl = '/sapi1.jpg'; // Default fallback image
              
              try {
                const imagesResponse = await apiClient.get(
                  API_ENDPOINTS.PRODUK.PUBLIC_IMAGES(produk.id)
                );
                
                if (
                  imagesResponse.data &&
                  imagesResponse.data.images &&
                  Array.isArray(imagesResponse.data.images) &&
                  imagesResponse.data.images.length > 0
                ) {
                  // Ambil image pertama
                  const firstImage = imagesResponse.data.images[0];
                  // Build full URL jika url relatif
                  if (firstImage.url) {
                    imageUrl = getImageUrl(firstImage.url);
                  }
                }
              } catch (imageError) {
                console.warn(`Error fetching image for produk ${produk.id}:`, imageError);
                // Tetap gunakan default image
              }
              
              // Format harga untuk display
              const formatPrice = (price) => {
                if (!price || price === 0) return '0';
                return new Intl.NumberFormat('id-ID').format(price);
              };
              
              return {
                id: produk.id,
                code: produk.id,
                breed: produk.jenis_produk_detail_nama || produk.jenis_produk_nama || 'Sapi',
                name: produk.nama || `Produk ${produk.id}`,
                weight: produk.target_bobot ? `${produk.target_bobot}Kg` : '-',
                price: formatPrice(produk.harga_jual || produk.harga_beli),
                farm: produk.kandang_nama || 'Kandang PakMoo',
                category: produk.id_kategori_produk,
                categoryName: produk.kategori_produk_nama,
                image: imageUrl,
                isAvailable: produk.is_available,
                isSold: produk.is_sold || false,
                hargaJual: produk.harga_jual,
                hargaBeli: produk.harga_beli,
              };
            })
        );
        
        setCowsData(produksWithImages);
      } catch (error) {
        console.error('Error fetching produk:', error);
        setCowsData([]);
        setPagination({ limit: 12, offset: 0, total: 0, has_next: false, has_prev: false });
      } finally {
        setCowsLoading(false);
      }
    };

    fetchProduk();
  }, [selectedCategory, currentPage]);

  // Reset ke halaman 1 ketika kategori berubah
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  const currentCows = cowsData;
  const totalPages = Math.ceil(pagination.total / itemsPerPage);

  const handleCowClick = (cowId) => {
    navigate(`/katalog/${cowId}`);
  };

  const handleContactClick = (e, cowId) => {
    e.stopPropagation();
    // Handle contact action
    window.open('https://wa.me/6281234567890', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner - Promotional */}
      <div className="bg-gradient-to-br from-cream-50 via-cream-100 to-amber-50 pt-36 sm:pt-48 lg:pt-56 pb-16 sm:pb-20 lg:pb-24 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full -mr-48 -mt-48 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full -ml-48 -mb-48 blur-3xl"></div>
        
        {/* Wave Decoration at Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1200 120" className="w-full h-24 text-secondary/20" preserveAspectRatio="none">
            <path d="M0,80 C150,60 300,100 450,75 C600,50 750,85 900,70 C1050,55 1200,75 1200,75 L1200,120 L0,120 Z" fill="currentColor"/>
          </svg>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-12">
            {/* Left Content */}
            <div className="flex-1 text-center lg:text-left space-y-4 sm:space-y-6">
              {/* Logo Badge */}
              <div className="inline-flex items-center space-x-2 mb-4 sm:mb-6">
                <div className="bg-primary px-3 sm:px-4 py-2 rounded-lg shadow-md">
                  <span className="text-accent font-bold text-xs sm:text-sm">PAK MOO</span>
                </div>
                <div className="text-[10px] sm:text-xs text-gray-600 font-medium uppercase tracking-wide">
                  PETERNAKAN AGROWISATA KEMITRAAN MODERN
                </div>
              </div>
              
              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-bold text-primary leading-tight">
                Semua Bisa Qurban
              </h1>
              
              {/* Call to Action Text */}
              <div className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold leading-tight sm:leading-snug">
                <span className="text-accent">Yuk Ikut </span>
                <span className="text-primary">Tabungan Qurban 2025</span>
              </div>
              
              {/* Description */}
              <p className="text-sm sm:text-base lg:text-xl text-gray-700 font-medium">
                Lebih Ringan, Lebih Terencana, Lebih Bermanfaat
              </p>
            </div>
            
            {/* Right Visual Section - PAK MOO FARM Card */}
            <div className="flex-shrink-0 flex items-center justify-center lg:justify-end w-full sm:w-auto">
              {/* PAK MOO FARM Card */}
              <div className="bg-white/90 backdrop-blur-md rounded-2xl p-5 sm:p-6 lg:p-8 shadow-2xl border border-white/50 transform hover:scale-105 transition-transform duration-300 max-w-xs sm:max-w-sm">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg">
                    <i className="fa-solid fa-home text-white text-xl sm:text-2xl lg:text-3xl"></i>
                  </div>
                  <div>
                    <div className="text-[10px] sm:text-xs lg:text-sm text-gray-600 font-medium uppercase tracking-wide mb-1">
                      PAK MOO
                    </div>
                    <div className="text-lg sm:text-xl lg:text-2xl font-bold text-primary">
                      FARM
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="container mx-auto px-6 lg:px-12 py-12">
        {/* Categories */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Kategori</h3>
          {categoriesLoading ? (
            <div className="flex flex-wrap gap-3">
              {/* Loading skeleton */}
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="px-6 py-3 rounded-full border-2 border-gray-200 bg-gray-100 animate-pulse"
                  style={{ width: '120px', height: '44px' }}
                ></div>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-full border-2 transition-all duration-300 flex items-center space-x-2 ${
                    selectedCategory === category.id
                      ? 'bg-accent border-accent text-primary font-semibold'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-accent hover:text-accent'
                  }`}
                >
                  <i className={`fa-solid ${category.icon}`}></i>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Products Grid */}
        {cowsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                <div className="h-64 bg-gray-200"></div>
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : currentCows.length === 0 ? (
          <div className="text-center py-12">
            <i className="fa-solid fa-cow text-gray-300 text-6xl mb-4"></i>
            <p className="text-gray-600 text-lg">Tidak ada produk tersedia</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {currentCows.map((cow) => (
            <div
              key={cow.id}
              onClick={() => handleCowClick(cow.id)}
              className="bg-white rounded-2xl shadow-lg border-t-4 border-accent overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
            >
              {/* Product Code */}
              <div className="px-4 py-2 bg-primary text-white text-sm font-semibold">
                {cow.code}
              </div>
              
              {/* Cow Image */}
              <div className="relative">
                <img
                  src={cow.image}
                  alt={cow.name}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute bottom-2 left-2 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  #IngatQurban #IngatPakMoo
                </div>
                {cow.isSold && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Terjual
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2">
                  {cow.name}
                </h3>
                <div className="space-y-1 text-sm text-gray-600">
                  <div>Harga: <span className="font-semibold text-red-600">Rp {cow.price}</span></div>
                  <div>Lokasi: {cow.farm}</div>
                  {cow.isSold && (
                    <div className="text-red-600 font-semibold text-xs">
                      <i className="fa-solid fa-check-circle mr-1"></i>
                      Terjual
                    </div>
                  )}
                </div>
                
                <button
                  onClick={(e) => handleContactClick(e, cow.id)}
                  className="w-full mt-4 bg-primary text-white py-2 px-4 rounded-full font-semibold hover:bg-secondary transition-colors duration-300 flex items-center justify-center space-x-2"
                >
                  <i className="fa-solid fa-phone"></i>
                  <span>Hubungi Kami</span>
                </button>
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Pagination */}
        {!cowsLoading && totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={!pagination.has_prev}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  currentPage === page
                    ? 'bg-accent text-primary font-semibold'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {page}
              </button>
            ))}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={!pagination.has_next}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Catalog;
